import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Button, Input, Row, Col, Select, DatePicker, AutoComplete } from 'antd';
import { getTriggerPointOptions } from 'common/helpers/getTriggerPointOptions';
import { dayFormatType, formatDay } from '~helpers/day';
import { orderReceivableStatus, bookingJourneyFilter } from '~constants/order';
import * as actions from '~actions/reconciliation';
import { convertEmptyArrayToNull } from 'common/helpers/convertEmptyArrayToNull';

const OrderFilter = ({ t, form, queryList, setFilters, filters }) => {
  const dispatch = useDispatch();
  const { getFieldDecorator, validateFields } = form;
  const dateFormat = dayFormatType.UKFORMAT;
  const [showMore, setShowMore] = useState(true);
  const [landlordData, setLandlordData] = useState([]);
  const [propertyData, setPropertyData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [cityData, setCityData] = useState([]);

  const handleFiliter = () => {
    validateFields((err, values) => {
      if (!err) {
        if (values) {
          const variables = {
            citySlug: values?.citySlug ?? null,
            countrySlug: values?.countrySlug ?? null,
            landlordSlug: values?.landlordSlug ?? null,
            propertySlug: values?.propertySlug ?? null,
            invoiceNumber: isEmpty(values?.invoiceNumber) ? null : values?.invoiceNumber,
            orderReferenceId: isEmpty(values?.orderReferenceId) ? null : values?.orderReferenceId,
            receivableStatus:
              values?.receivableStatus?.length > 0 ? values?.receivableStatus : null,
            moveInDateStart: values.moveInDate[0]
              ? formatDay(dayFormatType.FILTERPARAMS, values.moveInDate[0])
              : null,
            moveInDateEnd: values.moveInDate[1]
              ? formatDay(dayFormatType.FILTERPARAMS, values.moveInDate[1])
              : null,
            moveOutDateStart: values.moveOutDate[0]
              ? formatDay(dayFormatType.FILTERPARAMS, values.moveOutDate[0])
              : null,
            moveOutDateEnd: values.moveOutDate[1]
              ? formatDay(dayFormatType.FILTERPARAMS, values.moveOutDate[1])
              : null,
            pageSize: filters?.pageSize,
            pageNumber: 1,
            bookingJourneys: values?.bookingJourneys?.length > 0 ? values?.bookingJourneys : null,
            commissionTriggerPoint: convertEmptyArrayToNull(values?.commissionTriggerPoint) ?? null,
          };

          setFilters({ ...filters, ...variables });
          queryList({ variables });
        }
      }
    });
  };

  const handleResetBtn = () => {
    form.resetFields();
    setFilters({});
    queryList();
  };

  const handleShowMoreBtn = () => {
    setShowMore(!showMore);
  };

  const handleQueryLandlord = value => {
    dispatch(
      actions.searchLandlordList(value, res => {
        const data = res?.search?.edges ?? [];
        setLandlordData(data);
      }),
    );
  };

  const handleQueryProperty = value => {
    dispatch(
      actions.searchPropertyList(value, res => {
        const data = res?.search?.edges ?? [];
        setPropertyData(data);
      }),
    );
  };

  const handleQueryCountry = value => {
    dispatch(
      actions.searchCountryList(value, res => {
        const data = res?.search?.edges ?? [];
        setCountryData(data);
      }),
    );
  };

  const handleQueryCity = value => {
    dispatch(
      actions.searchCityList(value, res => {
        const data = res?.search?.edges ?? [];
        setCityData(data);
      }),
    );
  };

  useEffect(() => {
    if (isEmpty(filters)) {
      form.resetFields();
    }
  }, [filters]);

  return (
    <div className="reconciliation__filter" style={{ paddingBottom: showMore ? 12 : 0 }}>
      <Form className="reconciliation__filter__form">
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.reconcil_status')}
            >
              {getFieldDecorator('receivableStatus', {
                initialValue: filters?.receivableStatus ?? [],
              })(
                <Select
                  mode="multiple"
                  maxTagCount={window.outerWidth > 1440 ? 1 : 0}
                  placeholder={t('cms.reconciliation.lead_generation.filter.reconcil_status')}
                >
                  <For of={Object.keys(orderReceivableStatus)} each="type">
                    <Select.Option value={type} key={type}>
                      {t(
                        `cms.reconciliation.lead_generation.table.filter.status.${type?.toLowerCase()}`,
                      )}
                    </Select.Option>
                  </For>
                </Select>,
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.landlord')}
            >
              {getFieldDecorator('landlordSlug', {
                initialValue: filters?.landlordSlug ?? null,
              })(
                <AutoComplete
                  onChange={handleQueryLandlord}
                  placeholder={t('cms.reconciliation.lead_generation.filter.landlord.placeholder')}
                >
                  <For of={landlordData} each="item">
                    <AutoComplete.Option key={item?.node?.id} value={item?.node?.slug}>
                      {item?.node?.name}
                    </AutoComplete.Option>
                  </For>
                </AutoComplete>,
              )}
            </Form.Item>
          </Col>
          <Col
            span={8}
            className={classNames({
              'reconciliation__filter__col--hidden': showMore,
            })}
          >
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.property')}
            >
              {getFieldDecorator('propertySlug', {
                initialValue: filters?.propertySlug ?? null,
              })(
                <AutoComplete
                  onChange={handleQueryProperty}
                  placeholder={t('cms.reconciliation.lead_generation.filter.property.placeholder')}
                >
                  <For of={propertyData} each="item">
                    <AutoComplete.Option key={item?.node?.id} value={item?.node?.slug}>
                      {item?.node?.name}
                    </AutoComplete.Option>
                  </For>
                </AutoComplete>,
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row
          gutter={24}
          className={classNames({
            'reconciliation__filter__row--hidden': showMore,
          })}
        >
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.booking_number')}
            >
              {getFieldDecorator('orderReferenceId', {
                initialValue: filters?.orderReferenceId ?? null,
              })(
                <Input
                  placeholder={t(
                    'cms.reconciliation.lead_generation.filter.booking_number.placeholder',
                  )}
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.destination_country')}
            >
              {getFieldDecorator('countrySlug', {
                initialValue: filters?.countrySlug ?? null,
              })(
                <AutoComplete
                  onChange={handleQueryCountry}
                  placeholder={t(
                    'cms.reconciliation.lead_generation.filter.destination_country.placeholder',
                  )}
                >
                  <For of={countryData} each="item">
                    <AutoComplete.Option key={item?.node?.id} value={item?.node?.slug}>
                      {item?.node?.name}
                    </AutoComplete.Option>
                  </For>
                </AutoComplete>,
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.destination_city')}
            >
              {getFieldDecorator('citySlug', {
                initialValue: filters?.citySlug ?? null,
              })(
                <AutoComplete
                  onChange={handleQueryCity}
                  placeholder={t(
                    'cms.reconciliation.lead_generation.filter.destination_city.placeholder',
                  )}
                >
                  <For of={cityData} each="item">
                    <AutoComplete.Option key={item?.node?.slug} value={item?.node?.slug}>
                      {item?.node?.name}
                    </AutoComplete.Option>
                  </For>
                </AutoComplete>,
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row
          gutter={24}
          className={classNames({
            'reconciliation__filter__row--hidden': showMore,
          })}
        >
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.landlord.detail.listing_plan.label')}
            >
              {getFieldDecorator('bookingJourneys', {
                initialValue: filters?.bookingJourneys ?? [],
              })(
                <Select
                  mode="multiple"
                  maxTagCount={window.outerWidth > 1440 ? 1 : 0}
                  placeholder={t('cms.landlord.detail.listing_plan.label')}
                >
                  <For of={Object.keys(bookingJourneyFilter)} each="type">
                    <Select.Option value={type} key={type}>
                      {t(
                        `cms.reconciliation.lead_generation.table.filter.status.${type?.toLowerCase()}`,
                      )}
                    </Select.Option>
                  </For>
                </Select>,
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.move_in_date')}
            >
              {getFieldDecorator('moveInDate', {
                initialValue: [
                  filters?.moveInDateStart ? moment(filters?.moveInDateStart) : null,
                  filters?.moveInDateEnd ? moment(filters?.moveInDateEnd) : null,
                ],
              })(<DatePicker.RangePicker format={dateFormat} />)}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.move_out_date')}
            >
              {getFieldDecorator('moveOutDate', {
                initialValue: [
                  filters?.moveOutDateStart ? moment(filters?.moveOutDate) : null,
                  filters?.moveOutDateEnd ? moment(filters?.moveOutDateEnd) : null,
                ],
              })(<DatePicker.RangePicker format={dateFormat} />)}
            </Form.Item>
          </Col>
        </Row>
        <Row
          gutter={24}
          className={classNames({
            'reconciliation__filter__row--hidden': showMore,
          })}
        >
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.invoice_number')}
            >
              {getFieldDecorator('invoiceNumber', {
                initialValue: filters?.invoiceNumber ?? null,
              })(
                <Input
                  placeholder={t(
                    'cms.reconciliation.lead_generation.detail.table.field.invoice_number.placeholder',
                  )}
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="reconciliation__filter__item"
              label={t('cms.reconciliation.lead_generation.filter.commission_trigger_point')}
            >
              {getFieldDecorator('commissionTriggerPoint', {
                initialValue: filters?.commissionTriggerPoint ?? [],
              })(
                <Select
                  mode="single"
                  placeholder={t(
                    'cms.reconciliation.lead_generation.filter.commission_trigger_point',
                  )}
                >
                  <For of={getTriggerPointOptions(t)} each="type">
                    <Select.Option value={type.value} key={type.value}>
                      {type.text}
                    </Select.Option>
                  </For>
                </Select>,
              )}
            </Form.Item>
          </Col>
        </Row>
        <div
          className={classNames(
            showMore ? 'reconciliation__filter__btns' : 'reconciliation__filter__btns-more',
          )}
        >
          <Button onClick={handleResetBtn}>
            {t('cms.billing.transfer.table.filter.reset.btn')}
          </Button>
          <Button
            type="primary"
            style={{ margin: '0 12px' }}
            htmlType="submit"
            onClick={handleFiliter}
          >
            {t('cms.billing.transfer.table.filter.search.btn')}
          </Button>
          <button onClick={handleShowMoreBtn} className="reconciliation__filter__btn-more">
            {t('cms.reconciliation.lead_generation.table.filter.more.btn')}
            <LegacyIcon type={showMore ? 'down' : 'up'} className="receive__filter__btn-icon" />
          </button>
        </div>
      </Form>
    </div>
  );
};

OrderFilter.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.object,
  filters: PropTypes.object,
  setFilters: PropTypes.func,
  queryList: PropTypes.func,
};

OrderFilter.defaultProps = {
  t: () => {},
  form: {},
  setFilters: () => {},
  queryList: () => {},
  filters: {},
};

export default Form.create()(OrderFilter);
